import { Config as BaseConfig } from '@va/http-client';
import '@va/styles/index.scss';
import '@va/styles/tailwind.css';
import Config from 'Config';
import moment from 'moment';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';

import { createStore } from '@va/store';
import rootReducer from 'wix/reducers';
import rootSaga from './sagas/index';

import { appHistory } from '@va/shared/router';
import { debounce, extractLanguage, getParameterByName, updateUrlParameter } from '@va/util/helpers';

import { DEFAULT_NON_AWL_SUPPORT_CENTER_URL, nonTranslatableText } from '@va/constants';
import { initialize, set, setIgnoreVisitsLink } from '@va/dashboard/actions/app';
import { setCustomizationOptions, setLocale, setWindowWidth } from '@va/dashboard/actions/ui';
import { ChatwootWidget, NotificationV2 } from '@va/dashboard/components';
import { DashboardGlobalFiltersConfigProvider, DashboardReportBlockConfigProvider } from '@va/dashboard/shared/filters';
import { useDefaultTranslationOptions } from '@va/dashboard/util-hooks';
import { getTranslationsUrl, TranslationsProvider, useLocalizationContext } from '@va/localization';
import { RemoveAdblockerPage } from '@va/shared/remove-adblocker-page';
import { ConfirmationDialogContextProvider, RouterWarningProvider } from '@va/shared/util-confirmation';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { LoaderBar } from '@va/ui/design-system';
import { ErrorBoundary } from '@va/util/components';
import { DashboardAppWorker } from '@va/util/misc';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { PropsWithChildren, StrictMode, useEffect } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { SWRConfig } from 'swr';
import { getRoutes } from './router/routes';

const locale = extractLanguage();
moment.locale(locale);

const getAuthenticateHeaders = (store: any) => () => {
  const state = store.getState();
  const auth = state.getIn(['core', 'authentication']).toJS();
  const headers = {} as Record<string, unknown>;

  if (auth.token) {
    headers['Authorization'] = 'Bearer ' + auth.token;
  }

  return headers;
};

const store = createStore(rootReducer, rootSaga);
BaseConfig.setApiBaseUrl(Config.apiUrl);
window.addEventListener(
  'resize',
  debounce(() => {
    store.dispatch(setWindowWidth(window.innerWidth));
  }, 150),
);
const isPublishQueryParam = getParameterByName('isPublish');
store.dispatch(
  set({
    instance: getParameterByName('instance'),
    siteUrl: decodeURIComponent(getParameterByName('siteUrl') ?? ''),
    isPublish: isPublishQueryParam ? decodeURIComponent(isPublishQueryParam) : 'true',
    locale: locale,
  }),
);
BaseConfig.setAuthProvider(getAuthenticateHeaders(store) as any);
store.dispatch(initialize());
store.dispatch(setIgnoreVisitsLink(Config.wixIgnoreVisitsUrl));
store.dispatch(setWindowWidth(window.innerWidth));
// Zoom out when using the app within the WiX iframe
// if (window.parent) {
//   const windowWidth = window.innerWidth;

//   if (windowWidth > 1024 && windowWidth <= 1440) {
//     document.documentElement.style.zoom = '75%';
//   }
// }

store.dispatch(
  setCustomizationOptions({
    providerName: nonTranslatableText.appName,
    supportCenterDomain: DEFAULT_NON_AWL_SUPPORT_CENTER_URL,
    chatwootToken: Config.chatwootToken,
  }),
);

const fetchTranslationsFn = async (locale = 'en') => {
  const response = await fetch(getTranslationsUrl(locale));
  const responseText = await response.text();
  return JSON.parse(responseText.replace(/\\n/g, '<br/>'));
};

const WixApp = () => {
  useEffect(() => {
    //call run method on worker instance to track all visits on visa app
    DashboardAppWorker.run();
  }, []);

  return (
    <Providers>
      <App />
    </Providers>
  );
};

export default WixApp;

const Providers = ({ children }: PropsWithChildren) => {
  return (
    <StrictMode>
      <FlagsmithProvider flagsmith={flagsmith}>
        <SWRConfig value={{ revalidateIfStale: false, revalidateOnFocus: false }}>
          <SkeletonTheme baseColor='#F9F9F9' highlightColor='#EEEEEE'>
            <Provider store={store}>
              <TranslationsProvider
                onLocaleChange={(locale) => {
                  store.dispatch(setLocale(locale));
                }}
                fetchTranslations={fetchTranslationsFn}
                updateURL={(newLocale) => {
                  const updatedURL = updateUrlParameter(T3_URL_PARAMS.locale, newLocale);
                  appHistory.replace(updatedURL);
                }}
                useDefaultOptions={useDefaultTranslationOptions}
              >
                <DashboardReportBlockConfigProvider>
                  <ConfirmationDialogContextProvider>
                    <DashboardGlobalFiltersConfigProvider>{children}</DashboardGlobalFiltersConfigProvider>
                  </ConfirmationDialogContextProvider>
                </DashboardReportBlockConfigProvider>
              </TranslationsProvider>
            </Provider>
          </SkeletonTheme>
        </SWRConfig>
      </FlagsmithProvider>
    </StrictMode>
  );
};

const App = () => {
  const { isLoading: translationsLoading, translationsError } = useLocalizationContext();
  if (translationsLoading) return <LoaderBar />;
  if (translationsError) return <RemoveAdblockerPage />;

  return (
    <Router history={appHistory}>
      <ErrorBoundary>
        <ChatwootWidget forcedLocale='en' />
        <NotificationV2 />
        <ConfirmationDialogContextProvider>
          <RouterWarningProvider>{getRoutes()}</RouterWarningProvider>
        </ConfirmationDialogContextProvider>
      </ErrorBoundary>
    </Router>
  );
};
